import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import TutorScheduleCalendar from '../../components/tutor/TutorScheduleCalendar';
import { tutorApiClient } from '../../tutorApiClient';
import { hostApiClient } from '../../hostApiClient';
import {
  CreateHostTutorBookingRequest,
  CreateManualTutorBookingResponse,
  CreateTutorCalendarEventRequest,
  UserTutorProfileSettings,
} from '../../models/Tutor';
import { PaymentMethod, type DiscountSettings } from '../../models/Organization';
import {
  hasAnyTutorWorkingHours,
  TUTOR_WORKING_HOURS_SECTION_ID,
  tutorWorkingHoursToSpaceWeeklySchedule,
} from '../../utils/tutorWorkingHours';
import { loadTutorCalendarEventsForRange, TutorCalendarLessonEvent } from '../../utils/tutorCalendarEvents';

const DEFAULT_TIMEZONE = 'Europe/Warsaw';

export default function TutorCalendarPage() {
  const { t } = useTranslation();
  const [profile, setProfile] = useState<UserTutorProfileSettings | null>(null);
  const [events, setEvents] = useState<TutorCalendarLessonEvent[]>([]);
  const [loadingProfile, setLoadingProfile] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [range, setRange] = useState<{ start: Date; end: Date } | null>(null);
  const [refreshToken, setRefreshToken] = useState(0);
  const [discountSettings, setDiscountSettings] = useState<DiscountSettings | null>(null);

  useEffect(() => {
    let cancelled = false;
    hostApiClient
      .getOrganization()
      .then((organization) => {
        if (!cancelled) setDiscountSettings(organization.discountSettings ?? null);
      })
      .catch(() => {
        if (!cancelled) setDiscountSettings(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    setLoadingProfile(true);
    tutorApiClient
      .getTutorProfileSettings()
      .then((data) => {
        if (!cancelled) setProfile(data);
      })
      .catch(() => {
        if (!cancelled) setProfile(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingProfile(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const tutorOption = useMemo(() => {
    if (!profile) return null;
    return {
      tutorId: profile.tutorId,
      name: profile.headline?.trim() || t('tutorCalendar.mySchedule', 'My schedule'),
      timezone: profile.timezone,
      sessionTypes: profile.supportedSessionTypes,
    };
  }, [profile, t]);

  const weeklySchedule = useMemo(() => {
    if (!profile?.workingHours || !hasAnyTutorWorkingHours(profile.workingHours)) {
      return null;
    }
    return tutorWorkingHoursToSpaceWeeklySchedule(profile.workingHours);
  }, [profile?.workingHours]);

  const timezone = profile?.timezone?.trim() || DEFAULT_TIMEZONE;

  const fetchEvents = useCallback(
    async (start: Date, end: Date) => {
      if (!profile?.tutorId) {
        setEvents([]);
        return;
      }
      setLoadingEvents(true);
      try {
        const loaded = await loadTutorCalendarEventsForRange(
          profile.tutorId,
          start,
          end,
          timezone,
          tutorApiClient.getTutorBookingRequests.bind(tutorApiClient),
          t('tutorCalendar.externalBlock', 'Google Calendar'),
          t('tutorCalendar.lessonFallback', 'Lesson'),
        );
        setEvents(loaded);
      } catch {
        setEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    },
    [profile?.tutorId, t, timezone],
  );

  useEffect(() => {
    if (!range || !profile?.tutorId) return;
    void fetchEvents(range.start, range.end);
  }, [range, profile?.tutorId, fetchEvents, refreshToken]);

  const handleRangeChange = useCallback((start: Date, end: Date) => {
    setRange({ start, end });
  }, []);

  const handleCreateManualBlock = useCallback(async (request: CreateTutorCalendarEventRequest) => {
    await tutorApiClient.createTutorCalendarEvent(request);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleDeleteManualBlock = useCallback(async (calendarEventId: string) => {
    await tutorApiClient.deleteTutorCalendarEvent(calendarEventId);
    setRefreshToken((prev) => prev + 1);
  }, []);

  const handleCreateManualBooking = useCallback(
    async (request: CreateHostTutorBookingRequest): Promise<CreateManualTutorBookingResponse> => {
      const response = await hostApiClient.createManualTutorBooking({
        ...request,
        paymentMethod: PaymentMethod.CASH,
      });
      setRefreshToken((prev) => prev + 1);
      return response;
    },
    [],
  );

  return (
    <TutorScheduleCalendar
      mode="tutor"
      title={t('tutorCalendar.title', 'Lesson schedule')}
      emptyStateTitle={t('tutorCalendar.noProfile', 'Tutor profile not ready')}
      emptyStateHint={t(
        'tutorCalendar.noProfileHint',
        'Finish your listing and working hours to see your lesson calendar.',
      )}
      tutors={tutorOption ? [tutorOption] : []}
      selectedTutor={tutorOption}
      weeklySchedule={weeklySchedule}
      timezone={timezone}
      events={events}
      loading={loadingProfile || loadingEvents}
      onRangeChange={handleRangeChange}
      editSchedulePath={`/tutor/listing#${TUTOR_WORKING_HOURS_SECTION_ID}`}
      onCreateManualBlock={handleCreateManualBlock}
      onDeleteManualBlock={handleDeleteManualBlock}
      onCreateWalkIn={handleCreateManualBooking}
      walkInLessonOptions={profile?.lessonOptions ?? []}
      walkInDiscountSettings={discountSettings}
    />
  );
}
